import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOnboarding } from '@/hooks/useOnboarding';
import { MetierSelectionModal } from '@/components/onboarding/MetierSelectionModal';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { isInFreeTrial } from '@/lib/freeTrial';

/**
 * Page d'onboarding B8ild
 * Affiche uniquement la sélection du métier BTP, puis redirige vers le dashboard
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { checkOnboardingCompleted, completeOnboarding } = useOnboarding();

  // Rediriger si pas connecté
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  // Rediriger si onboarding déjà complété
  useEffect(() => {
    if (!authLoading && user) {
      const onboardingCompletedInMetadata = user.user_metadata?.onboarding_completed === true;

      if (onboardingCompletedInMetadata || checkOnboardingCompleted()) {
        if (isInFreeTrial(user)) {
          navigate('/dashboard');
        } else {
          navigate('/payment-setup');
        }
      }
    }
  }, [user, authLoading, checkOnboardingCompleted, navigate]);

  // Loader pendant le chargement auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Appelé après sélection du métier dans le modal
  const handleMetierSelected = async () => {
    completeOnboarding();

    // Sauvegarder dans les métadonnées utilisateur : onboarding + free trial
    await supabase.auth.updateUser({
      data: {
        onboarding_completed: true,
        free_trial_started: new Date().toISOString(),
        free_trial_plan: 'pro',
        payment_setup_completed: true,
      }
    });

    // Forcer le refresh de la session pour que useAuth() ait les metadata à jour
    await supabase.auth.refreshSession();

    navigate('/dashboard');
  };

  // Afficher directement le modal de sélection du métier
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <MetierSelectionModal
        open={true}
        onComplete={handleMetierSelected}
      />
    </div>
  );
}
